import dayjs from 'dayjs';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import {
	Box,
	Flex,
	Group,
	LoadingOverlay,
	Pagination,
	ScrollArea,
	Switch,
	Table,
	Text,
} from '@mantine/core';

import { useQuery } from '@tanstack/react-query';

import RestrictionsItem from '@/components/restrictions-item/restrictions-item';

import { restrictionApi } from '@/shared/api/restriction';
import { chunk } from '@/shared/helpers/utils.ts';
import { IRestriction } from '@/shared/types/restriction.types.ts';
import { SearchInput } from '@/shared/uikit/components/input';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

const getFilteredRestrictions = (restrictions: IRestriction[] | undefined, searchValue: string) => {
	const search = searchValue.toLowerCase();
	return restrictions?.filter(
		(restriction) =>
			restriction?.booking?.booking_object.toString().toLowerCase().includes(search) ||
			restriction?.person?.full_name.toString().toLowerCase().includes(search) ||
			restriction?.car_number?.toLowerCase().includes(search),
	);
};

const Restrictions = () => {
	const [activePage, setPage] = useState(1);
	const [data, setData] = useState<IRestriction[] | []>([]);
	const [isYesterday, setYesterday] = useState(true);
	const [isProcessed, setIsProcessed] = useState(false);
	const [searchValue, setSearchValue] = useState('');

	const { data: restrictions, isLoading: isRestrictionsLoading } = useQuery({
		queryKey: ['restrictions'],
		queryFn: restrictionApi.getRestrictions,
		refetchOnMount: true,
		refetchOnReconnect: true,
		refetchOnWindowFocus: true,
	});

	const someCarPass = useMemo(() => data?.some((el) => el?.car_number), [data]);

	const handleTodayChange = (evt: ChangeEvent<HTMLInputElement>) => {
		setYesterday(evt.target.checked);
	};

	const handleShowProcessed = (evt: ChangeEvent<HTMLInputElement>) => {
		setIsProcessed(evt.target.checked);
	};

	const handleSearch = useCallback(
		(evt: ChangeEvent<HTMLInputElement>) => setSearchValue(evt.currentTarget?.value),
		[],
	);

	const filterRestrictions = useMemo(() => {
		switch (true) {
			case isYesterday && !isProcessed: {
				const filtered = restrictions?.filter((restriction) => {
					return (
						dayjs.utc(restriction.fixation_date).local().format('YYYY-MM-DD') ===
							dayjs().add(-1, 'day').format('YYYY-MM-DD') && restriction.state === 'waiting'
					);
				});

				return filtered?.sort(
					(a, b) => dayjs(b.booking?.start).unix() - dayjs(a.booking?.start).unix(),
				);
			}

			case isYesterday && isProcessed:
				return restrictions
					?.filter((restriction) => {
						return (
							dayjs.utc(restriction.fixation_date).local().format('YYYY-MM-DD') ===
								dayjs().add(-1, 'day').format('YYYY-MM-DD') && restriction.state !== 'waiting'
						);
					})
					.sort((a, b) => dayjs(b.booking?.start).unix() - dayjs(a.booking?.start).unix());

			case !isYesterday && isProcessed:
				return restrictions
					?.filter((restriction) => {
						return restriction.state !== 'waiting';
					})
					.sort((a, b) => dayjs(b.booking?.start).unix() - dayjs(a.booking?.start).unix());

			default:
				return restrictions
					?.filter((restriction) => {
						return restriction.state === 'waiting';
					})
					.sort((a, b) => dayjs(b.booking?.start).unix() - dayjs(a.booking?.start).unix());
		}
	}, [restrictions, isProcessed, isYesterday]);

	const chunked = chunk(data ?? [], 10);

	useEffect(() => {
		if (restrictions) {
			setData(filterRestrictions ?? []);
		}
	}, [restrictions, isYesterday, isProcessed]);

	useEffect(() => {
		if (!searchValue) {
			setData(filterRestrictions ?? []);
			return;
		}

		setData(getFilteredRestrictions(filterRestrictions, searchValue) ?? []);
	}, [searchValue, restrictions, isYesterday, isProcessed]);

	const rows = chunked[activePage - 1]?.map((restriction) => (
		<RestrictionsItem key={restriction.id} restriction={restriction} someCarPass={someCarPass} />
	));

	return (
		<LayoutRow
			title={{
				title: { children: 'Нарушения', size: 'h4', fw: 800 },
			}}
			type="once"
			style={{ height: 'calc(100vh - 116px)' }}
		>
			<Box pos="relative" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				<LoadingOverlay
					visible={isRestrictionsLoading}
					zIndex={1000}
					overlayProps={{ radius: 2, blur: 2 }}
				/>
				<Group>
					<SearchInput
						w={530}
						value={searchValue}
						onChange={handleSearch}
						placeholder="Поиск по нарушениям"
						helpText="Введите любое значение из возможных или его часть: номер машины, ФИО сотрудника, название объекта и т.д."
					/>

					<Group gap={16}>
						<Group gap={8}>
							<Switch size="md" checked={isYesterday} onChange={handleTodayChange} />
							<Text size="md">Вчера</Text>
						</Group>
						<Group gap={8}>
							<Switch size="md" checked={isProcessed} onChange={handleShowProcessed} />
							<Text size="md">Показать обработанные нарушения</Text>
						</Group>
					</Group>
				</Group>
				{data.length > 0 ? (
					<>
						<ScrollArea h={'100dvh'} pr={'md'} scrollbarSize={12} offsetScrollbars>
							<Table verticalSpacing="md" stickyHeader>
								<Table.Thead>
									<Table.Tr>
										<Table.Th>
											<Text size="md" c="dimmed">
												Клиент
											</Text>
										</Table.Th>
										<Table.Th>
											<Text size="md" c="dimmed">
												Объект
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Номер авто
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Начало
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Конец
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Статус нарушения
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Действия
											</Text>
										</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>{rows}</Table.Tbody>
							</Table>
						</ScrollArea>
						<Pagination total={chunked.length} value={activePage} onChange={setPage} mt="sm" />
					</>
				) : (
					<Flex direction="column" align="center" justify="center" h={'calc(100vh - 300px)'}>
						<Text fz={18} fw={700}>
							Пока ничего нет 😔
						</Text>
					</Flex>
				)}
			</Box>
		</LayoutRow>
	);
};
export default Restrictions;
